import { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import { getSummary, getJobs, getJobsByCompany, getJob, generateDocs, getGenerated, openGeneratedDoc, setAuthToken, setJobStatus, updateApplicationStatus, updateApplicationNotes, addComment, saveGenerated, getMe, getAdminOverview, getAdminActivity, getQueryEffectiveness, getIngestAddress, getSearchConfig, updateSearchConfig, getApplicationFacts, updateApplicationFacts, getVersions, getVersion, restoreVersion, getDistilled, runDistill, getTask, getActivityFeed, getJobTimeline } from "./api";

// Capture a ?job=<id> deep link at MODULE LOAD, before react-oidc-context (or
// its onSigninCallback) can strip the query string. The extension's "open in
// Jobuna" link relies on this surviving until the authed App applies it.
const INITIAL_JOB_PARAM = new URLSearchParams(window.location.search).get("job");

// The seeker IA (redesign #52): three top-level areas. Positions holds every job
// in the pipeline, split into Open (in flight) and Closed (set aside); each gets
// its own list with columns suited to it. Costs/models/timing live in Admin.
const SEEKER_AREAS = [
  { key: "positions", label: "Positions" },
  { key: "applications", label: "Applications" },
  { key: "activity", label: "Activity" },
];
const POSITION_TABS = [
  { key: "open", label: "Open" },
  { key: "closed", label: "Closed" },
];
// The statuses that surface under Positions > Open (mirrors OPEN_VIEW_STATUSES in
// apps/api/src/stages.ts; the web bundle can't import server modules).
const OPEN_VIEW_STATUSES = ["identified", "scoring", "processing", "ready_for_review", "approved", "needs_attention"];
const STATUS_LABEL = {
  identified: "identified", scoring: "scoring", processing: "processing",
  ready_for_review: "ready for review", approved: "approved", applied: "applied",
  // needs_attention rides inside Processing; the seeker never sees "failed".
  skipped: "skipped", needs_attention: "Technical Issue Being Reviewed",
};
// Processing sub-status labels (jobs.processing_state), mirrors PROCESSING_STATES
// in stages.ts. Shown in the Open list's Stage column when one is set.
const PROCESSING_STATE_LABEL = {
  waiting: "Waiting in Queue", gen_resume: "Generating Resume", gen_cover: "Generating Cover Letter",
  regen_resume: "Regenerating Resume", regen_cover: "Regenerating Cover Letter",
  needs_attention: "Technical Issue Being Reviewed",
};
// What the Open list's Stage column shows: the processing sub-status if the job
// carries one, else the stage label.
const stageLabel = (j) => {
  if ((j.status === "processing" || j.status === "needs_attention") && j.processing_state) {
    return PROCESSING_STATE_LABEL[j.processing_state] || STATUS_LABEL[j.status] || j.status;
  }
  return STATUS_LABEL[j.status] || j.status;
};
// Skip-category labels + who-closed-it text for the Closed view. Mirrors the API
// source of truth in apps/api/src/stages.ts (SKIP_CATEGORIES).
const SKIP_CATEGORY_LABEL = {
  no_longer_available: "Job no longer available", position_filled: "Position filled",
  posting_expired: "Posting expired", too_junior: "Too junior", too_senior: "Too senior",
  comp_too_low: "Compensation too low", location: "Location",
  company_not_interested: "Company (not interested)", already_applied: "Already applied",
  duplicate: "Duplicate", not_interested: "Not interested", other: "Other",
  below_threshold: "Below score threshold", blocked_company: "Blocked company",
  duplicate_existing: "Duplicate of an existing job", not_qualified: "Not qualified",
  invalid: "Posting no longer available",
};
const closedByLabel = (by) => (by === "system" ? "Jobuna" : "You");
// Typeahead suggestions for the free-text "Why skip?" reason (the user can still
// type anything). Drawn from the common user-skip categories (#61).
const SKIP_REASON_SUGGESTIONS = [
  "Not interested", "Compensation too low", "Location", "Too junior", "Too senior",
  "Company (not interested)", "Already applied", "Duplicate", "Position filled",
  "Posting expired", "Not qualified",
];
const shortDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "");
// Application funnel sub-statuses [value, label], in order.
const APP_STATUSES = [
  ["submitted", "Applied"], ["received_response", "Received response"],
  ["interview_scheduled", "Interview scheduled"], ["interviewing", "Interviewing"],
  ["interviewed", "Interviewed"],
  ["offered", "Offer received"], ["offer_accepted", "Offer accepted"],
  ["offer_declined", "Offer declined"], ["not_selected", "Not selected"],
  ["withdrew", "Withdrew"],
];
const appLabel = (v) => (APP_STATUSES.find(([k]) => k === v)?.[1]) || v || "Submitted";

// Sum the summary counts for a set of statuses (the Open tab spans several
// stages; Closed and Applications are single-status).
const sumStatuses = (summary, keys) =>
  keys.reduce((a, k) => a + (summary.find((x) => x.status === k)?.n ?? 0), 0);

const usd = (n) => "$" + (Number(n) || 0).toFixed(2);
const num = (n) => (Number(n) || 0).toLocaleString();
const when = (d) => (d ? new Date(d).toLocaleString() : "");

// Wraps App with Cognito auth: gates the app behind sign-in and feeds the ID
// token to the API client. index.js renders this only when Cognito is configured.
export function AuthedApp() {
  const auth = useAuth();
  const [silentTried, setSilentTried] = useState(false);

  // Cold-start renewal (jobuna#68 follow-up). `automaticSilentRenew` only
  // refreshes the token while the app is open; it does not help a session that
  // already expired while the browser was closed. So after an overnight close
  // the stored user loads already-expired, isAuthenticated is false, and the
  // user gets bounced to the hosted login despite a still-valid (30-day) refresh
  // token. Here we redeem that refresh token once on startup via signinSilent
  // (the refresh_token grant needs no iframe/silent_redirect_uri), so only a real
  // logout, token revoke, or refresh-token expiry forces a new sign-in.
  useEffect(() => {
    if (auth.isLoading || auth.activeNavigator || auth.isAuthenticated || silentTried) return;
    // Only attempt if a prior session is on disk to refresh from; otherwise the
    // user has genuinely never signed in (or signed out) and should see the gate.
    if (!auth.user) { setSilentTried(true); return; }
    setSilentTried(true);
    auth.signinSilent().catch(() => { /* refresh token gone/revoked -> fall through to the Sign in gate */ });
  }, [auth.isLoading, auth.activeNavigator, auth.isAuthenticated, auth.user, silentTried, auth]);

  // Keep showing Loading (not the Sign in gate) while the initial load runs or a
  // cold-start silent renewal is in flight, so a returning user never flashes the
  // login screen before the refresh completes.
  const renewing = auth.activeNavigator === "signinSilent" || (!silentTried && !!auth.user && !auth.isAuthenticated);
  if (auth.isLoading || renewing) {
    return <div className="app"><div className="brand">Jobuna</div><div className="sub">Loading…</div></div>;
  }
  if (auth.error) {
    return (
      <div className="app">
        <div className="brand">Jobuna</div>
        <div className="sub" style={{ color: "#f87171" }}>Sign-in error: {auth.error.message}</div>
        <button className="btn" onClick={() => auth.signinRedirect()}>Try again</button>
      </div>
    );
  }
  if (!auth.isAuthenticated) {
    return (
      <div className="app">
        <div className="brand">Jobuna</div>
        <div className="sub">Your AI job-search copilot.</div>
        <div style={{ marginTop: 16 }}><button className="btn" onClick={() => auth.signinRedirect()}>Sign in</button></div>
      </div>
    );
  }
  // Set the token during render (before App's child effects fire) so the first
  // API calls carry it.
  setAuthToken(auth.user?.id_token);
  // Sign out must also end the Cognito hosted-UI session, not just the local
  // token, or the next sign-in silently reuses the Cognito cookie. Redirect
  // through Cognito's /logout (logout_uri must match a registered logout URL).
  const signOut = () => {
    setAuthToken(null);
    const domain = process.env.REACT_APP_COGNITO_DOMAIN;
    const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
    const done = () => {
      if (domain && clientId) {
        window.location.assign(`${domain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(window.location.origin)}`);
      } else {
        window.location.reload();
      }
    };
    auth.removeUser().finally(done);
  };
  return <App user={{ email: auth.user?.profile?.email }} onSignOut={signOut} />;
}

export default function App({ user, onSignOut } = {}) {
  const [me, setMe] = useState({ email: user?.email, isAdmin: false });
  const [mode, setMode] = useState("seeker"); // "seeker" | "admin"
  const [area, setArea] = useState("positions");  // "positions" | "applications" | "activity"
  const [posTab, setPosTab] = useState("open");    // "open" | "closed" (within Positions)
  const [summary, setSummary] = useState([]);
  const [selected, setSelected] = useState(null);
  // Company name whose "all roles" modal is open, or null (issue #5).
  const [company, setCompany] = useState(null);
  // Bumped after any change so the active view re-fetches.
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => { getMe().then(setMe).catch(() => {}); }, []);
  const loadSummary = () => getSummary().then(setSummary).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadSummary(); }, [refreshKey]);

  const openJob = (id) => getJob(id).then(setSelected).catch(() => {});
  const openCompany = (name) => setCompany(name);

  // Deep link: jobuna.ai/?job=<id> (e.g. a row click in the browser extension)
  // opens that job's detail on load, then strips the param so a later refresh
  // doesn't re-trigger it.
  useEffect(() => {
    if (INITIAL_JOB_PARAM) {
      openJob(INITIAL_JOB_PARAM);
      const url = new URL(window.location.href);
      url.searchParams.delete("job");
      window.history.replaceState({}, "", url.pathname + url.search + url.hash);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Called by Detail after a status change: refresh counts + the active view,
  // and reflect the new status in the open panel.
  const onChanged = (jobId, newStatus) => {
    setRefreshKey((k) => k + 1);
    if (newStatus && selected?.job?.id === jobId) {
      setSelected((s) => ({ ...s, job: { ...s.job, status: newStatus } }));
    }
  };

  const total = summary.reduce((a, s) => a + s.n, 0);

  return (
    <div className={`app${selected ? " detail-open" : ""}`}>
      <div className="brand">
        Jobuna
        <span style={{ float: "right", fontSize: 13, fontWeight: 400 }}>
          {me.isAdmin && (
            <span className="modetoggle">
              <span className={mode === "seeker" ? "on" : ""} onClick={() => setMode("seeker")}>Job search</span>
              <span className={mode === "admin" ? "on" : ""} onClick={() => setMode("admin")}>Admin</span>
            </span>
          )}
          {onSignOut && <span className="close" style={{ cursor: "pointer", marginLeft: 14 }} onClick={onSignOut} title="Sign out">Sign out</span>}
        </span>
      </div>
      <div className="sub">{me.email || user?.email || "Steve Wasula"}{mode === "seeker" ? ` · ${total} jobs` : " · admin dashboard"}</div>

      {mode === "admin" ? (
        <AdminView refreshKey={refreshKey} onOpen={openJob} />
      ) : (
        <>
          <IngestAddressBar />
          <ApplicationFactsPanel />
          <LearnedPrefs refreshKey={refreshKey} />
          <div className="tabs">
            {SEEKER_AREAS.map((a) => {
              const count = a.key === "applications" ? sumStatuses(summary, ["applied"]) : null;
              return (
                <span key={a.key} className={`tab ${area === a.key ? "active" : ""}`} onClick={() => setArea(a.key)}>
                  {a.label}{count !== null && <span className="n">{count}</span>}
                </span>
              );
            })}
          </div>
          {area === "positions" && (
            <>
              <div className="subtabs">
                {POSITION_TABS.map((t) => (
                  <span key={t.key} className={`subtab ${posTab === t.key ? "active" : ""}`} onClick={() => setPosTab(t.key)}>
                    {t.label}
                    <span className="n">{t.key === "open" ? sumStatuses(summary, OPEN_VIEW_STATUSES) : sumStatuses(summary, ["skipped"])}</span>
                  </span>
                ))}
              </div>
              {posTab === "open"
                ? <OpenView onOpen={openJob} onCompany={openCompany} refreshKey={refreshKey} />
                : <ClosedView onOpen={openJob} onCompany={openCompany} onChanged={onChanged} refreshKey={refreshKey} />}
            </>
          )}
          {area === "applications" && <AppliedView onOpen={openJob} onCompany={openCompany} refreshKey={refreshKey} />}
          {area === "activity" && <ActivityView refreshKey={refreshKey} onOpen={openJob} />}
        </>
      )}

      {selected && <Detail key={selected.job.id} data={selected} onClose={() => setSelected(null)} onChanged={onChanged} onCompany={openCompany} showCost={mode === "admin"} />}
      {company && (
        <CompanyModal
          company={company}
          onClose={() => setCompany(null)}
          onOpen={(id) => { setCompany(null); openJob(id); }}
        />
      )}
    </div>
  );
}

// One company cell that, when onCompany is provided, becomes a link opening the
// "all roles at this company" modal (issue #5). stopPropagation so it doesn't
// also trigger the row's open-job click.
function CompanyCell({ name, onCompany, onClick }) {
  if (!onCompany || !name) return <td onClick={onClick}>{name}</td>;
  return (
    <td onClick={onClick}>
      <button className="link" title={`All roles at ${name}`}
        onClick={(e) => { e.stopPropagation(); onCompany(name); }}>{name}</button>
    </td>
  );
}

// All roles from one company across every status (issue #5): see other open
// roles, avoid re-applying, and reconsider past skips before deciding.
function CompanyModal({ company, onClose, onOpen }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    getJobsByCompany(company).then((j) => { setJobs(j); setLoading(false); }).catch(() => setLoading(false));
  }, [company]);
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <span className="close" onClick={onClose}>×</span>
        <h2>{company}</h2>
        <div className="sub" style={{ marginBottom: 12 }}>Every role from {company}, across all statuses.</div>
        <table>
          <thead><tr><th>Score</th><th>Title</th><th>Stage</th><th>Docs</th></tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="4" className="sub">Loading…</td></tr>
            ) : jobs.length === 0 ? (
              <tr><td colSpan="4" className="sub">No jobs.</td></tr>
            ) : jobs.map((j) => (
              <tr key={j.id} className="row" onClick={() => onOpen(j.id)}>
                <td className="score">{j.score ?? "—"}</td>
                <td>{j.title}</td>
                <td><span className={`badge ${j.status}`}>{STATUS_LABEL[j.status] || j.status}</span></td>
                <td>{j.has_docs ? <span className="dot" title="Tailored resume + cover ready">●</span> : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Shows the tenant's email ingest address: forward job alerts here (or point a
// job-board alert at it) and they flow into the funnel. No inbox access needed.
function IngestAddressBar() {
  const [addr, setAddr] = useState(null);
  const [copied, setCopied] = useState(false);
  const [relay, setRelay] = useState(null);
  const [autoApprove, setAutoApprove] = useState(null);
  const [autogenScore, setAutogenScore] = useState(null);
  const [minScore, setMinScore] = useState(null);
  useEffect(() => {
    getIngestAddress().then((r) => setAddr(r.address)).catch(() => {});
    getSearchConfig().then((c) => {
      setRelay(Boolean(c.relayCopy));
      setAutoApprove(Boolean(c.autoApprove));
      setAutogenScore(c.autogenScoreThreshold ?? 75);
      setMinScore(c.minScore ?? 55);
    }).catch(() => {});
  }, []);
  if (!addr) return null;
  const copy = () => {
    navigator.clipboard?.writeText(addr).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  };
  const toggleRelay = () => {
    const next = !relay;
    setRelay(next);
    updateSearchConfig({ relayCopy: next }).catch(() => setRelay(!next));
  };
  const toggleAutoApprove = () => {
    const next = !autoApprove;
    setAutoApprove(next);
    updateSearchConfig({ autoApprove: next }).catch(() => setAutoApprove(!next));
  };
  return (
    <div className="ingest-bar">
      <span className="sub">Forward job alerts to</span>
      <code onClick={copy} title="Click to copy">{addr}</code>
      <button className="link" onClick={copy}>{copied ? "Copied" : "Copy"}</button>
      {relay !== null && (
        <label className="relay-toggle" title="Also send a copy of each forwarded alert to your account email">
          <input type="checkbox" checked={relay} onChange={toggleRelay} />
          <span className="sub">copy to my inbox</span>
        </label>
      )}
      {autoApprove !== null && (
        <label className="relay-toggle" style={{ marginLeft: 0 }} title="Skip review: generated drafts go straight to Approved">
          <input type="checkbox" checked={autoApprove} onChange={toggleAutoApprove} />
          <span className="sub">auto-approve drafts</span>
        </label>
      )}
      {autogenScore !== null && (
        <label className="relay-toggle" style={{ marginLeft: 0 }} title="Automatically tailor a resume and cover letter for any job scoring at least this. Lower the number to generate for more jobs.">
          <span className="sub">auto-generate at score</span>
          <input type="number" min="0" max="100" value={autogenScore}
            onChange={(e) => setAutogenScore(e.target.value)}
            onBlur={() => {
              const n = Math.max(0, Math.min(100, Math.round(Number(autogenScore) || 0)));
              setAutogenScore(n);
              updateSearchConfig({ autogenScoreThreshold: n }).catch(() => {});
            }}
            style={{ width: 52 }} />
          <span className="sub">+</span>
        </label>
      )}
      {minScore !== null && (
        <label className="relay-toggle" style={{ marginLeft: 0 }} title="Jobs scoring below this are set aside automatically (below the bar). Raise it to be pickier, lower it to see more.">
          <span className="sub">set aside below score</span>
          <input type="number" min="0" max="100" value={minScore}
            onChange={(e) => setMinScore(e.target.value)}
            onBlur={() => {
              const n = Math.max(0, Math.min(100, Math.round(Number(minScore) || 0)));
              setMinScore(n);
              updateSearchConfig({ minScore: n }).catch(() => {});
            }}
            style={{ width: 52 }} />
        </label>
      )}
    </div>
  );
}

const EMPTY_APPLICATION_FACTS = {
  currentEmployer: "",
  authorizedToWorkUs: null,
  requiresSponsorship: null,
  fillSensitiveAllowed: false,
  eeoGender: "",
  eeoRaceEthnicity: "",
  eeoVeteranStatus: "",
  eeoDisabilityStatus: "",
};

const boolSelectValue = (value) => (value === true ? "true" : value === false ? "false" : "");
const boolFromSelect = (value) => (value === "true" ? true : value === "false" ? false : null);

function ApplicationFactsPanel() {
  const [facts, setFacts] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    getApplicationFacts().then((f) => setFacts({ ...EMPTY_APPLICATION_FACTS, ...f })).catch(() => setFacts(null));
  }, []);
  if (!facts) return null;

  const patch = (next) => setFacts((f) => ({ ...f, ...next }));
  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const out = await updateApplicationFacts(facts);
      setFacts({ ...EMPTY_APPLICATION_FACTS, ...out });
      setSaved(true);
      setTimeout(() => setSaved(false), 1600);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="profile-facts">
      <button className="link profile-facts-head" onClick={() => setOpen((o) => !o)}>
        <span className="caret">{open ? "▾" : "▸"}</span> Application autofill profile
      </button>
      <span className="sub">
        {facts.fillSensitiveAllowed ? "EEO autofill allowed" : "EEO autofill off"}
      </span>
      {open && (
        <div className="profile-facts-body">
          <label>
            <span className="sub">Most recent employer</span>
            <input className="input" value={facts.currentEmployer || ""}
              onChange={(e) => patch({ currentEmployer: e.target.value })} placeholder="Company name" />
          </label>
          <label>
            <span className="sub">Authorized to work in the US</span>
            <select className="input" value={boolSelectValue(facts.authorizedToWorkUs)}
              onChange={(e) => patch({ authorizedToWorkUs: boolFromSelect(e.target.value) })}>
              <option value="">Do not autofill</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </label>
          <label>
            <span className="sub">Need sponsorship now or in the future</span>
            <select className="input" value={boolSelectValue(facts.requiresSponsorship)}
              onChange={(e) => patch({ requiresSponsorship: boolFromSelect(e.target.value) })}>
              <option value="">Do not autofill</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </label>
          <label className="sensitive-consent">
            <input type="checkbox" checked={Boolean(facts.fillSensitiveAllowed)}
              onChange={(e) => patch({ fillSensitiveAllowed: e.target.checked })} />
            <span>Allow Jobuna to autofill saved EEO and sensitive answers</span>
          </label>
          {facts.fillSensitiveAllowed && (
            <>
              <label>
                <span className="sub">Gender answer</span>
                <input className="input" value={facts.eeoGender || ""}
                  onChange={(e) => patch({ eeoGender: e.target.value })} placeholder="Example: Decline to self-identify" />
              </label>
              <label>
                <span className="sub">Race / ethnicity answer</span>
                <input className="input" value={facts.eeoRaceEthnicity || ""}
                  onChange={(e) => patch({ eeoRaceEthnicity: e.target.value })} placeholder="Example: Decline to self-identify" />
              </label>
              <label>
                <span className="sub">Veteran status answer</span>
                <input className="input" value={facts.eeoVeteranStatus || ""}
                  onChange={(e) => patch({ eeoVeteranStatus: e.target.value })} placeholder="Example: I do not wish to answer" />
              </label>
              <label>
                <span className="sub">Disability status answer</span>
                <input className="input" value={facts.eeoDisabilityStatus || ""}
                  onChange={(e) => patch({ eeoDisabilityStatus: e.target.value })} placeholder="Example: I do not wish to answer" />
              </label>
            </>
          )}
          <div>
            <button className="btn" onClick={save} disabled={saving}>{saving ? "Saving..." : "Save autofill profile"}</button>
            {saved && <span className="sub">Saved</span>}
          </div>
        </div>
      )}
    </div>
  );
}

// What Jobuna has learned from the seeker's own edits, skip reasons, and
// document comments, rolled into the preferences that steer future generation.
// Seeker-appropriate (it's the user's own preferences; no cost/model/timing).
function LearnedPrefs({ refreshKey }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = () => getDistilled().then(setData).catch(() => setData(null));
  useEffect(() => { load(); }, [refreshKey]);
  if (!data) return null;

  const bullets = (data.distilledNotes || "")
    .split("\n").map((l) => l.replace(/^[-*]\s+/, "").trim()).filter(Boolean);

  const refresh = async () => {
    setBusy(true);
    try {
      const { taskId } = await runDistill();
      // Poll the task to completion, then reload the notes.
      for (let i = 0; i < 40; i++) {
        await new Promise((r) => setTimeout(r, 1500));
        const t = await getTask(taskId).catch(() => null);
        if (t && (t.status === "done" || t.status === "failed" || t.status === "dead")) break;
      }
      await load();
    } finally { setBusy(false); }
  };

  return (
    <div className="learned-bar">
      <span className="learned-head" onClick={() => setOpen((o) => !o)} title="Preferences Jobuna learned from your edits and feedback">
        <span className="caret">{open ? "▾" : "▸"}</span> What Jobuna has learned
        {data.pendingSignals > 0 && <span className="n">{data.pendingSignals} new</span>}
      </span>
      <button className="link" onClick={refresh} disabled={busy}>
        {busy ? "Updating…" : "Refresh from my feedback"}
      </button>
      {open && (
        bullets.length ? (
          <ul className="learned-list">{bullets.map((b, i) => <li key={i}>{b}</li>)}</ul>
        ) : (
          <div className="sub learned-empty">
            Nothing learned yet. As you edit drafts, give regenerate feedback, comment on a resume or cover, or skip jobs with a reason, Jobuna distills those into preferences that steer future drafts.
          </div>
        )
      )}
    </div>
  );
}

// Open table columns (#62). get() extracts the sortable/filterable value; type
// drives the comparator (#63); filter:true adds an Excel-style checkbox picklist
// (#64). Order: Score, Title, Company, Compensation Range, Stage, Found (no Docs).
const OPEN_COLUMNS = [
  { key: "score", label: "Score", type: "number", get: (j) => j.score },
  { key: "title", label: "Title", type: "string", get: (j) => j.title || "" },
  { key: "company", label: "Company", type: "string", get: (j) => j.company || "", filter: true },
  { key: "comp", label: "Compensation Range", type: "string", get: (j) => j.comp_estimate || "" },
  { key: "stage", label: "Stage", type: "string", get: (j) => stageLabel(j), filter: true },
  { key: "found", label: "Found", type: "date", get: (j) => j.discovered_at },
];

// Typed comparator so Score sorts numerically, Found chronologically (as a Date,
// not lexically), and the rest as strings (#63).
function compareBy(type, a, b) {
  if (type === "number") return (a == null ? -Infinity : Number(a)) - (b == null ? -Infinity : Number(b));
  if (type === "date") return new Date(a || 0).getTime() - new Date(b || 0).getTime();
  return String(a || "").localeCompare(String(b || ""));
}

// Excel-style checkbox picklist for one column (#64): distinct values, multi-select,
// select all / clear. A fixed backdrop catches click-away.
function FilterPopover({ values, isHidden, onToggle, onAll, onNone, onClose }) {
  return (
    <>
      <div onClick={(e) => { e.stopPropagation(); onClose(); }} style={{ position: "fixed", inset: 0, zIndex: 20 }} />
      <div onClick={(e) => e.stopPropagation()} style={{ position: "absolute", top: "100%", left: 0, zIndex: 21, marginTop: 4, background: "var(--panel)", border: "1px solid var(--line)", borderRadius: 8, padding: 10, minWidth: 190, maxHeight: 280, overflowY: "auto", boxShadow: "0 8px 24px rgba(0,0,0,.45)", textTransform: "none", fontWeight: 400 }}>
        <div style={{ display: "flex", gap: 10, marginBottom: 8 }}>
          <button className="link" onClick={onAll}>Select all</button>
          <button className="link" onClick={onNone}>Clear</button>
        </div>
        {values.length === 0 ? <div className="sub">No values</div> : values.map((v) => (
          <label key={v} style={{ display: "flex", alignItems: "center", gap: 8, padding: "3px 0", cursor: "pointer", color: "var(--text)" }}>
            <input type="checkbox" checked={!isHidden(v)} onChange={() => onToggle(v)} />
            <span>{v}</span>
          </label>
        ))}
      </div>
    </>
  );
}

// Positions > Open: every in-flight job in one list (Identified -> Approved, plus
// needs_attention shown calmly as "Technical Issue Being Reviewed"). Columns are
// sortable (#63) and Company/Stage are filterable (#64); default sort is highest
// score first. Found answers "when did Jobuna turn this up for me?".
function OpenView({ onOpen, onCompany, refreshKey }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState("score");
  const [sortDir, setSortDir] = useState("desc");
  const [hidden, setHidden] = useState({});          // { colKey: Set(hidden values) }
  const [openFilter, setOpenFilter] = useState(null); // colKey whose popover is open
  useEffect(() => {
    setLoading(true);
    getJobs("open").then((j) => { setJobs(j); setLoading(false); }).catch(() => setLoading(false));
  }, [refreshKey]);

  const colOf = (k) => OPEN_COLUMNS.find((c) => c.key === k);
  const toggleSort = (k) => {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir(k === "score" || k === "found" ? "desc" : "asc"); }
  };
  const distinct = (k) => [...new Set(jobs.map((j) => colOf(k).get(j)).filter((v) => v !== "" && v != null))]
    .sort((a, b) => String(a).localeCompare(String(b)));
  const isHidden = (k, v) => Boolean(hidden[k] && hidden[k].has(v));
  const toggleValue = (k, v) => setHidden((h) => {
    const s = new Set(h[k] || []);
    if (s.has(v)) s.delete(v); else s.add(v);
    return { ...h, [k]: s };
  });
  const setAll = (k, hide) => setHidden((h) => ({ ...h, [k]: hide ? new Set(distinct(k)) : new Set() }));
  const filterActive = (k) => Boolean(hidden[k] && hidden[k].size > 0);

  const visible = jobs.filter((j) => OPEN_COLUMNS.every((c) => !c.filter || !isHidden(c.key, c.get(j))));
  const rows = [...visible].sort((a, b) => {
    const c = colOf(sortKey);
    const av = c.get(a), bv = c.get(b);
    // Blanks always sort to the bottom regardless of direction, so an empty
    // Company (or any empty cell) never scatters through the sort (#81).
    const aE = av == null || av === "", bE = bv == null || bv === "";
    if (aE !== bE) return aE ? 1 : -1;
    const d = compareBy(c.type, av, bv);
    return sortDir === "asc" ? d : -d;
  });
  const arrow = (k) => (sortKey === k ? (sortDir === "asc" ? " ▲" : " ▼") : "");

  return (
    <>
      <div className="sub" style={{ margin: "8px 0 14px" }}>
        Everything in flight for you. Click a column to sort; use the filters on Company and Stage to narrow the list.
      </div>
      <table>
        <thead>
          <tr>
            {OPEN_COLUMNS.map((c) => (
              <th key={c.key} style={{ position: "relative", whiteSpace: "nowrap" }}>
                <span style={{ cursor: "pointer", userSelect: "none" }} onClick={() => toggleSort(c.key)}>{c.label}{arrow(c.key)}</span>
                {c.filter && (
                  <button className="link" title={`Filter ${c.label}`}
                    style={{ marginLeft: 6, opacity: filterActive(c.key) ? 1 : 0.55 }}
                    onClick={(e) => { e.stopPropagation(); setOpenFilter((f) => (f === c.key ? null : c.key)); }}>{"☰"}</button>
                )}
                {openFilter === c.key && (
                  <FilterPopover values={distinct(c.key)} isHidden={(v) => isHidden(c.key, v)}
                    onToggle={(v) => toggleValue(c.key, v)} onAll={() => setAll(c.key, false)}
                    onNone={() => setAll(c.key, true)} onClose={() => setOpenFilter(null)} />
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? <tr><td colSpan="6" className="sub">Loading…</td></tr>
            : jobs.length === 0 ? <tr><td colSpan="6" className="sub">Nothing in flight yet. As Jobuna finds and scores roles, they show up here.</td></tr>
            : rows.length === 0 ? <tr><td colSpan="6" className="sub">No roles match the current filters.</td></tr>
            : rows.map((j) => (
              <tr key={j.id} className="row" onClick={() => onOpen(j.id)}>
                <td className="score">{j.score ?? "—"}</td>
                <td>{j.title}</td>
                <CompanyCell name={j.company} onCompany={onCompany} />
                <td className="sub">{j.comp_estimate || "—"}</td>
                <td><span className={`badge ${j.status}`}>{stageLabel(j)}</span></td>
                <td className="sub">{shortDate(j.discovered_at)}</td>
              </tr>
            ))}
        </tbody>
      </table>
    </>
  );
}

// Positions > Closed: jobs set aside, by you or by Jobuna. Never a silent
// graveyard - every row shows why and who closed it, and "Pursue anyway" recovers
// it back into the pipeline (PATCH status -> processing, which clears the skip).
function ClosedView({ onOpen, onCompany, onChanged, refreshKey }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  useEffect(() => {
    setLoading(true);
    getJobs("skipped").then((j) => { setJobs(j); setLoading(false); }).catch(() => setLoading(false));
  }, [refreshKey]);
  const pursue = async (id, e) => {
    e.stopPropagation();
    setBusyId(id);
    try {
      await setJobStatus(id, "processing");
      setJobs((js) => js.filter((j) => j.id !== id));  // it left the Closed bucket
      onChanged?.(id, "processing");
    } catch (_) { /* leave it in the list on failure */ }
    finally { setBusyId(null); }
  };
  return (
    <>
      <div className="sub" style={{ margin: "8px 0 14px" }}>
        Jobs set aside, by you or by Jobuna. Each shows why. Nothing here is gone for good: "Pursue anyway" puts it back in flight.
      </div>
      <table>
        <thead>
          <tr><th>Company</th><th>Title</th><th>Reason</th><th>Closed by</th><th>Date</th><th></th></tr>
        </thead>
        <tbody>
          {loading ? <tr><td colSpan="6" className="sub">Loading…</td></tr>
            : jobs.length === 0 ? <tr><td colSpan="6" className="sub">Nothing set aside.</td></tr>
            : jobs.map((j) => (
              <tr key={j.id} className="row" onClick={() => onOpen(j.id)}>
                <CompanyCell name={j.company} onCompany={onCompany} />
                <td>{j.title}</td>
                <td>{SKIP_CATEGORY_LABEL[j.skip_category] || "Set aside"}</td>
                <td><span className={`badge ${j.closed_by === "system" ? "system" : "user"}`}>{closedByLabel(j.closed_by)}</span></td>
                <td className="sub">{shortDate(j.discovered_at)}</td>
                <td onClick={(e) => e.stopPropagation()}>
                  <button className="link" disabled={busyId === j.id} onClick={(e) => pursue(j.id, e)}>
                    {busyId === j.id ? "…" : "Pursue anyway"}
                  </button>
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </>
  );
}

// Human "3m ago" relative time for the activity feed + per-job timeline.
function timeAgo(ts) {
  const d = (Date.now() - new Date(ts).getTime()) / 1000;
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}
// Account-level transparency feed: the warm narrative of everything Jobuna and
// the user have done. The antidote to "is it actually doing anything for me?".
function ActivityView({ refreshKey, onOpen }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    getActivityFeed(100).then((e) => { setEvents(e); setLoading(false); }).catch(() => setLoading(false));
  }, [refreshKey]);
  if (loading) return <div className="sub" style={{ margin: "10px 0" }}>Loading…</div>;
  if (!events.length) {
    return <div className="sub" style={{ margin: "10px 0" }}>
      Nothing yet. As Jobuna searches, scores, and tailors documents for you, every step shows up here.
    </div>;
  }
  return (
    <>
      <div className="sub" style={{ margin: "8px 0 14px" }}>Everything Jobuna and you have done for your search, newest first.</div>
      <ul className="activity">
        {events.map((e) => (
          <li key={e.id} className={`act ${e.actor}`}
              onClick={e.job_id ? () => onOpen(e.job_id) : undefined}
              style={e.job_id ? { cursor: "pointer" } : undefined}>
            <span className={`act-icon ${e.actor}`}>{e.actor === "user" ? "You" : "✦"}</span>
            <span className="act-msg">{e.message}</span>
            <span className="act-time">{timeAgo(e.created_at)}</span>
          </li>
        ))}
      </ul>
    </>
  );
}

// A single job's case file: its events in order, so the user sees the whole
// story of what Jobuna did for this role. Re-fetches when the job's status moves.
function Timeline({ jobId, statusKey }) {
  const [events, setEvents] = useState([]);
  useEffect(() => { getJobTimeline(jobId).then(setEvents).catch(() => {}); }, [jobId, statusKey]);
  if (!events.length) return null;
  return (
    <div className="timeline">
      <div className="label">Timeline</div>
      <ul>
        {events.map((e) => (
          <li key={e.id} className={`tl ${e.actor}`}>
            <span className="tl-msg">{e.message}</span>
            <span className="tl-time">{timeAgo(e.created_at)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Applied jobs with their real-world funnel status (submitted -> ... -> offer),
// editable inline.
function AppliedView({ onOpen, onCompany, refreshKey }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    getJobs("applied").then((j) => { setJobs(j); setLoading(false); }).catch(() => setLoading(false));
  }, [refreshKey]);
  const setAppStatus = (id, applicationStatus) => {
    // Optimistic: also bump the local "Updated" stamp so it reflects immediately.
    setJobs((js) => js.map((j) => (j.id === id ? { ...j, application_status: applicationStatus, application_status_at: new Date().toISOString() } : j)));
    updateApplicationStatus(id, applicationStatus).catch(() => {});
  };
  const saveNotes = (id, notes) => {
    setJobs((js) => js.map((j) => (j.id === id ? { ...j, application_notes: notes } : j)));
    updateApplicationNotes(id, notes).catch(() => {});
  };
  return (
    <>
      <div className="sub" style={{ margin: "8px 0 14px" }}>Your application pipeline: track each through the funnel, see when you applied and last updated it, and keep notes.</div>
      <table>
        <thead><tr><th>Score</th><th>Company</th><th>Title</th><th>Status</th><th>Applied</th><th>Updated</th><th>Notes</th></tr></thead>
        <tbody>
          {loading ? <tr><td colSpan="7" className="sub">Loading…</td></tr>
            : jobs.length === 0 ? <tr><td colSpan="7" className="sub">No applications yet.</td></tr>
            : jobs.map((j) => (
              <tr key={j.id} className="row">
                <td className="score" onClick={() => onOpen(j.id)}>{j.score ?? "—"}</td>
                <CompanyCell name={j.company} onCompany={onCompany} onClick={() => onOpen(j.id)} />
                <td onClick={() => onOpen(j.id)}>{j.title}</td>
                <td>
                  <select className="input" value={j.application_status || "submitted"}
                    onClick={(e) => e.stopPropagation()} onChange={(e) => setAppStatus(j.id, e.target.value)}>
                    {APP_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </td>
                <td className="sub">{shortDate(j.applied_at)}</td>
                <td className="sub">{shortDate(j.application_status_at)}</td>
                <td onClick={(e) => e.stopPropagation()}>
                  <input className="input" defaultValue={j.application_notes || ""} placeholder="Add a note…" style={{ minWidth: 150 }}
                    onBlur={(e) => { if (e.target.value !== (j.application_notes || "")) saveNotes(j.id, e.target.value); }} />
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </>
  );
}

function StatCard({ label, value, hint }) {
  return (
    <div className="card">
      <div className="card-label">{label}</div>
      <div className="card-value">{value}</div>
      {hint && <div className="sub" style={{ fontSize: 12 }}>{hint}</div>}
    </div>
  );
}

function Breakdown({ title, rows, keyName }) {
  return (
    <div style={{ flex: 1, minWidth: 200 }}>
      <div className="label">{title}</div>
      <table>
        <tbody>
          {(rows && rows.length) ? rows.map((r, i) => (
            <tr key={i}>
              <td>{r[keyName] || "—"}</td>
              <td className="sub" style={{ textAlign: "right" }}>{num(r.n)}</td>
              <td style={{ textAlign: "right" }}>{usd(r.usd)}</td>
            </tr>
          )) : <tr><td className="sub">none</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// Admin (business-owner) dashboard: platform-wide costs, model/stage/provider
// breakdowns, queue health, and the full event feed with model + timing. None of
// this appears in the job-seeker view.
function AdminView({ refreshKey, onOpen }) {
  const [ov, setOv] = useState(null);
  const [feed, setFeed] = useState([]);
  const [qeff, setQeff] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    Promise.all([
      getAdminOverview().catch(() => null),
      getAdminActivity().catch(() => []),
      getQueryEffectiveness().catch(() => []),
    ]).then(([o, a, qe]) => { setOv(o); setFeed(a || []); setQeff(qe || []); setLoading(false); });
  }, [refreshKey]);

  if (loading) return <div className="sub">Loading…</div>;
  if (!ov) return <div className="sub">Admin metrics unavailable.</div>;
  const sp = ov.spend || {};
  const queue = ov.queue || {};
  return (
    <>
      <div className="cards">
        <StatCard label="Spend today" value={usd(sp.today_usd)} />
        <StatCard label="Metered this month" value={usd(sp.metered_month_usd)} hint="counts against caps" />
        <StatCard label="All-time spend" value={usd(sp.total_usd)} hint={`${num(sp.events)} events`} />
        <StatCard label="Platform" value={num(ov.jobs)} hint={`${num(ov.tenants)} tenants · ${num(ov.users)} users · jobs`} />
      </div>

      <div className="cards">
        <Breakdown title="By provider" rows={ov.by_provider} keyName="provider" />
        <Breakdown title="By model" rows={ov.by_model} keyName="model" />
        <Breakdown title="By stage" rows={ov.by_stage} keyName="stage" />
        <div style={{ flex: 1, minWidth: 200 }}>
          <div className="label">Queue</div>
          <table>
            <tbody>
              {Object.keys(queue).length ? Object.entries(queue).map(([s, n]) => (
                <tr key={s}><td>{s}</td><td className="sub" style={{ textAlign: "right" }}>{num(n)}</td></tr>
              )) : <tr><td className="sub">empty</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {qeff.length > 0 && (
        <>
          <div className="label" style={{ marginTop: 8 }}>Search query effectiveness (cost per kept job · worst first)</div>
          <table>
            <thead>
              <tr><th>Query</th><th style={{ textAlign: "right" }}>Found</th><th style={{ textAlign: "right" }}>Kept</th><th style={{ textAlign: "right" }}>Skipped</th><th style={{ textAlign: "right" }}>Cost</th><th style={{ textAlign: "right" }}>$/kept</th></tr>
            </thead>
            <tbody>
              {qeff.map((q, i) => (
                <tr key={i}>
                  <td>{q.query}</td>
                  <td className="sub" style={{ textAlign: "right" }}>{num(q.found)}</td>
                  <td style={{ textAlign: "right" }}>{num(q.kept)}</td>
                  <td className="sub" style={{ textAlign: "right" }}>{num(q.skipped)}</td>
                  <td style={{ textAlign: "right" }}>{usd(q.costUsd)}</td>
                  <td style={{ textAlign: "right", color: q.kept === 0 && q.costUsd > 0 ? "#f87171" : undefined }}>
                    {q.costPerKept == null ? "—" : usd(q.costPerKept)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="sub" style={{ marginBottom: 6 }}>
            A query that keeps nothing but still costs (red) is a candidate to prune from your search config.
          </div>
        </>
      )}

      <div className="label" style={{ marginTop: 8 }}>Recent events (model · tokens · cost · timing)</div>
      <table>
        <thead>
          <tr><th>When</th><th>Job</th><th>Stage</th><th>Model</th><th>Tokens</th><th>Cost</th></tr>
        </thead>
        <tbody>
          {feed.length === 0 ? (
            <tr><td colSpan="6" className="sub">No events yet.</td></tr>
          ) : feed.map((e) => (
            <tr key={e.id} className={e.job_id ? "row" : ""} onClick={() => e.job_id && onOpen(e.job_id)}>
              <td className="sub">{when(e.created_at)}</td>
              <td>{e.company || "—"}</td>
              <td><span className="badge">{e.stage}</span></td>
              <td className="sub">{e.model}</td>
              <td className="sub">{num((e.input_tokens || 0) + (e.output_tokens || 0))}</td>
              <td>{usd(e.cost_usd)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="sub" style={{ marginTop: 10 }}>
        Spend is Bedrock model cost (provider cli = local Max dev, $0). Always-on AWS infrastructure (RDS, EB, CloudFront) is billed separately and is not usage based.
      </div>
    </>
  );
}

const PRE = { whiteSpace: "pre-wrap", fontFamily: "inherit", background: "#0b1220", padding: 12, borderRadius: 8 };
const TA = { ...PRE, width: "100%", minHeight: 220, color: "inherit", border: "1px solid #1e293b" };
const STATUS_ACTIONS = [
  { key: "approved", label: "Approve" },
  { key: "applied", label: "Mark applied" },
  { key: "skipped", label: "Skip" },
  { key: "processing", label: "Pursue anyway" },
];
const SOURCE_LABEL = { generation: "Generated", rewrite: "AI revised", edit: "Manual edit", restore: "Restored" };

function pct(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "0";
  return String(Math.max(0, Math.min(100, Math.round(v))));
}

function listItems(items) {
  const rows = Array.isArray(items) ? items.filter(Boolean) : [];
  if (!rows.length) return null;
  return <ul>{rows.map((x, i) => <li key={i}>{x}</li>)}</ul>;
}

function ScoreBreakdown({ job }) {
  const explanation = job.score_explanation || job.metadata?.score_explanation || null;
  if (job.score == null) return null;

  if (!explanation || !Array.isArray(explanation.areas) || explanation.areas.length === 0) {
    return (
      <div className="score-card">
        <div className="score-head">
          <div>
            <div className="label">Score transparency</div>
            <div className="score-total">{job.score}/100</div>
          </div>
          <span className="badge">legacy score</span>
        </div>
        <div className="sub">This older score does not have a detailed weighted breakdown yet. Re-score the job to generate one.</div>
      </div>
    );
  }

  return (
    <div className="score-card">
      <div className="score-head">
        <div>
          <div className="label">Score transparency</div>
          <div className="score-total">{explanation.totalScore ?? job.score}/100</div>
        </div>
        <span className="badge">{explanation.contributionTotal ?? job.score} weighted points</span>
      </div>
      {explanation.legacy && <div className="sub">Legacy score reconstructed from the original scoring buckets.</div>}
      <div className="score-drivers">
        {Array.isArray(explanation.positiveDrivers) && explanation.positiveDrivers.length > 0 && (
          <div><strong>Positive drivers</strong>{listItems(explanation.positiveDrivers)}</div>
        )}
        {Array.isArray(explanation.negativeDrivers) && explanation.negativeDrivers.length > 0 && (
          <div><strong>Watch-outs</strong>{listItems(explanation.negativeDrivers)}</div>
        )}
        {Array.isArray(explanation.missingEvidence) && explanation.missingEvidence.length > 0 && (
          <div><strong>Missing or uncertain</strong>{listItems(explanation.missingEvidence)}</div>
        )}
      </div>
      <div className="score-areas">
        {explanation.areas.map((area) => (
          <div className="score-area" key={area.key || area.label}>
            <div className="score-area-top">
              <div>
                <strong>{area.label}</strong>
                <div className="sub">{area.description}</div>
              </div>
              <div className="score-area-points">{area.weightedContribution}/{area.weight}</div>
            </div>
            <div className="score-meter" aria-label={`${area.label} raw score ${area.rawScore} percent`}>
              <span style={{ width: `${pct(area.rawScore)}%` }} />
            </div>
            <div className="sub">Raw subscore {Math.round(Number(area.rawScore) || 0)}/100 · weight {area.weight}</div>
            <div className="score-evidence">
              {listItems(area.evidence)}
              {listItems(area.positiveDrivers)}
              {listItems(area.negativeDrivers)}
              {listItems(area.missingEvidence)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Detail({ data, onClose, onChanged, onCompany, showCost }) {
  const { job, application, cost } = data;
  const m = job.metadata || {};
  const [gen, setGen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [comments, setComments] = useState(data.comments || []);
  const [newComment, setNewComment] = useState("");
  const [skipping, setSkipping] = useState(false);
  const [skipReason, setSkipReason] = useState("");
  const [feedback, setFeedback] = useState("");
  const [editing, setEditing] = useState(false);
  const [resumeText, setResumeText] = useState("");
  const [coverText, setCoverText] = useState("");
  const [saving, setSaving] = useState(false);
  const [versions, setVersions] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [viewing, setViewing] = useState(null); // a previewed old version, or null = current
  const [sel, setSel] = useState(null);          // pending inline comment { target, quote }
  const [appStatus, setAppStatusState] = useState(job.application_status || "submitted");

  // Show any previously generated draft when the job is opened.
  useEffect(() => { getGenerated(job.id).then((g) => { if (g) setGen(g); }).catch(() => {}); }, [job.id]);

  async function generate(fb) {
    setBusy(true); setErr(null);
    try {
      const g = await generateDocs(job.id, fb);
      if (g.error) setErr("Generation failed. Try again.");
      else { setGen(g); setEditing(false); setFeedback(""); setViewing(null); setVersions(null); }
    } catch (e) { setErr("Generation failed. Try again."); }
    finally { setBusy(false); }
  }

  async function changeStatus(status, reason) {
    try {
      await setJobStatus(job.id, status, reason);
      onChanged?.(job.id, status);
      setSkipping(false); setSkipReason("");
      // Skipping removes the job from the Open view, so close the detail panel
      // rather than leaving a now-set-aside job open on screen.
      if (status === "skipped") onClose?.();
    } catch (e) { setErr(`Could not update status. ${e?.message || ""}`.trim()); }
  }

  async function postComment() {
    const body = newComment.trim();
    if (!body) return;
    try {
      const c = await addComment(job.id, body, sel?.target || "job", sel?.quote || null);
      setComments((cs) => [...cs, c]);
      setNewComment("");
      setSel(null);
    } catch (e) { setErr("Could not add comment."); }
  }

  // Capture a text selection inside a doc so the next comment anchors to it.
  function captureSelection(target) {
    const text = (window.getSelection?.().toString() || "").trim();
    if (text) setSel({ target, quote: text.slice(0, 1000) });
  }

  async function loadHistory() {
    const next = !showHistory;
    setShowHistory(next);
    if (next && versions === null) {
      try { setVersions(await getVersions(job.id)); } catch (e) { setVersions([]); }
    }
  }
  async function openVersion(v) {
    try { setViewing({ id: v.id, ...(await getVersion(job.id, v.id)) }); } catch (e) { setErr("Could not load version."); }
  }
  async function restoreVer(vid) {
    try {
      const g = await restoreVersion(job.id, vid);
      setGen(g); setViewing(null); setEditing(false);
      setVersions(await getVersions(job.id));
    } catch (e) { setErr("Could not restore version."); }
  }

  function startEdit() {
    setResumeText(gen.resumeMarkdown || "");
    setCoverText(gen.coverMarkdown || "");
    setEditing(true);
  }
  async function saveEdits() {
    setSaving(true);
    try {
      const g = await saveGenerated(job.id, { resumeMarkdown: resumeText, coverMarkdown: coverText });
      setGen(g); setEditing(false); setVersions(null);
    } catch (e) { setErr("Could not save edits."); }
    finally { setSaving(false); }
  }

  return (
    <div className="detail">
      <span className="close" onClick={onClose}>×</span>
      <h2>{job.title}</h2>
      <div className="sub">
        {onCompany && job.company
          ? <button className="link" title={`All roles at ${job.company}`} onClick={() => onCompany(job.company)}>{job.company}</button>
          : job.company}
        {" · "}<span className={`badge ${job.status}`}>{STATUS_LABEL[job.status] || job.status}</span>{job.status === "applied" && job.application_status ? ` · ${appLabel(job.application_status)}` : ""} · score {job.score ?? "—"}
      </div>

      {/* Review actions */}
      <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
        {STATUS_ACTIONS
          .filter((a) => a.key !== job.status)
          // "Pursue anyway" recovers a SET-ASIDE job back into the pipeline, so it
          // only makes sense for a skipped job, never one already in flight (#81).
          .filter((a) => a.key !== "processing" || job.status === "skipped")
          .map((a) => (
            <button key={a.key} className={`btn ${a.key === "skipped" || a.key === "processing" ? "ghost" : ""}`}
              onClick={() => (a.key === "skipped" ? setSkipping(true) : changeStatus(a.key))}>{a.label}</button>
          ))}
      </div>

      {/* The case file: what Jobuna has done for this role, in order. */}
      <ScoreBreakdown job={job} />
      <Timeline jobId={job.id} statusKey={job.status} />
      {skipping && (
        <div style={{ marginTop: 8 }}>
          <input className="input" list="skip-reasons" placeholder="Why skip? (optional, helps tailoring)" value={skipReason}
            onChange={(e) => setSkipReason(e.target.value)} style={{ width: "70%" }} />
          <datalist id="skip-reasons">
            {SKIP_REASON_SUGGESTIONS.map((r) => <option key={r} value={r} />)}
          </datalist>
          <button className="btn" onClick={() => changeStatus("skipped", skipReason)}>Confirm skip</button>
          <button className="btn ghost" onClick={() => setSkipping(false)}>Cancel</button>
        </div>
      )}

      {job.status === "applied" && (
        <div style={{ marginTop: 10 }}>
          <div className="label">Application status</div>
          <select className="input" value={appStatus}
            onChange={(e) => { setAppStatusState(e.target.value); updateApplicationStatus(job.id, e.target.value).catch(() => {}); }}>
            {APP_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      )}

      {/* Downloads (#66): prefer the migrated/approved PDF (applications.resume_doc/
          cover_doc, presigned to download with a friendly filename); otherwise fall
          back to rendering the generated draft. So an imported job that has PDFs but
          no generated_documents still offers working Download Resume / Cover buttons. */}
      {(application?.resume_url || application?.cover_url || gen) && (
        <div style={{ marginTop: 14 }}>
          <div className="label">Documents</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {(application?.resume_url || gen) && (application?.resume_url
              ? <a className="btn" href={application.resume_url} target="_blank" rel="noreferrer">Download resume</a>
              : <button className="btn" onClick={() => openGeneratedDoc(job.id, "resume").catch(() => {})}>Download resume</button>)}
            {(application?.cover_url || gen) && (application?.cover_url
              ? <a className="btn ghost" href={application.cover_url} target="_blank" rel="noreferrer">Download cover letter</a>
              : <button className="btn ghost" onClick={() => openGeneratedDoc(job.id, "cover").catch(() => {})}>Download cover letter</button>)}
          </div>
        </div>
      )}

      <div style={{ marginTop: 10 }}>
        <button className="btn" onClick={() => generate()} disabled={busy}>
          {busy ? "Generating (about a minute)…" : gen ? "Regenerate" : "Generate tailored resume + cover letter"}
        </button>
        {err && <div className="sub" style={{ color: "#f87171" }}>{err}</div>}
        {showCost && cost && cost.runs > 0 && (
          <div className="sub" style={{ marginTop: 4 }}>
            {cost.runs} generation{cost.runs === 1 ? "" : "s"} · {usd(cost.total_usd)} · {num((cost.input_tokens || 0) + (cost.output_tokens || 0))} tokens
          </div>
        )}
      </div>

      {gen && (
        <>
          <div style={{ marginTop: 10 }}>
            {!editing && !viewing && <button className="btn ghost" onClick={startEdit}>Edit</button>}
            {editing && <button className="btn" onClick={saveEdits} disabled={saving}>{saving ? "Saving…" : "Save edits"}</button>}
            {editing && <button className="btn ghost" onClick={() => setEditing(false)}>Cancel</button>}
            {!editing && <button className="btn ghost" onClick={loadHistory}>{showHistory ? "Hide history" : "History"}</button>}
          </div>

          {showHistory && (
            <div style={{ marginTop: 8 }}>
              <div className="label">Edit history</div>
              {versions === null ? <div className="sub">Loading…</div>
                : versions.length === 0 ? <div className="sub">No versions yet.</div>
                : versions.map((v, i) => (
                  <div key={v.id} className="version-row">
                    <span className="sub">{SOURCE_LABEL[v.source] || v.source} · {new Date(v.createdAt).toLocaleString()}{i === 0 ? " · current" : ""}</span>
                    <span style={{ marginLeft: "auto", display: "flex", gap: 10 }}>
                      <button className="link" onClick={() => openVersion(v)}>View</button>
                      {i !== 0 && <button className="link" onClick={() => restoreVer(v.id)}>Restore</button>}
                    </span>
                  </div>
                ))}
            </div>
          )}

          {viewing && (
            <div className="viewing-banner">
              <span className="sub">Viewing {SOURCE_LABEL[viewing.source] || viewing.source} from {new Date(viewing.generatedAt).toLocaleString()} (read-only)</span>
              <button className="btn ghost" onClick={() => setViewing(null)}>Back to current</button>
            </div>
          )}

          {gen.generatedAt && !viewing && <div className="sub">Last saved {new Date(gen.generatedAt).toLocaleString()}</div>}

          <div className="label">Generated resume{!editing && !viewing && <span className="sub" style={{ fontWeight: 400 }}> · select text to comment on it</span>}</div>
          {editing
            ? <textarea style={TA} value={resumeText} onChange={(e) => setResumeText(e.target.value)} />
            : <pre style={PRE} onMouseUp={() => !viewing && captureSelection("resume")}>{(viewing || gen).resumeMarkdown}</pre>}
          <div className="label">Generated cover letter</div>
          {editing
            ? <textarea style={TA} value={coverText} onChange={(e) => setCoverText(e.target.value)} />
            : <pre style={PRE} onMouseUp={() => !viewing && captureSelection("cover")}>{(viewing || gen).coverMarkdown}</pre>}

          {!editing && !viewing && (
            <div style={{ marginTop: 10 }}>
              <div className="label">Regenerate with feedback</div>
              <textarea style={{ ...TA, minHeight: 70 }} placeholder="e.g. lead with the AI transformation story, cut the Salesforce detail, shorten the summary…"
                value={feedback} onChange={(e) => setFeedback(e.target.value)} />
              <button className="btn" onClick={() => generate(feedback)} disabled={busy || !feedback.trim()}>
                {busy ? "Regenerating…" : "Regenerate with this feedback"}
              </button>
            </div>
          )}
        </>
      )}

      {/* Comments */}
      <div className="label">Comments</div>
      {comments.length === 0 && <div className="sub">No comments yet.</div>}
      {comments.map((c) => (
        <div key={c.id} style={{ background: "#0b1220", padding: 8, borderRadius: 6, marginBottom: 6 }}>
          {c.target && c.target !== "job" && (
            <div className="sub" style={{ fontSize: 11, marginBottom: 4 }}>
              on {c.target}{c.quote ? `: “${c.quote.length > 120 ? c.quote.slice(0, 120) + "…" : c.quote}”` : ""}
            </div>
          )}
          <div>{c.body}</div>
          <div className="sub" style={{ fontSize: 11 }}>{new Date(c.created_at).toLocaleString()}</div>
        </div>
      ))}
      {sel && (
        <div className="sub" style={{ marginTop: 6 }}>
          Commenting on {sel.target}: “{sel.quote.length > 80 ? sel.quote.slice(0, 80) + "…" : sel.quote}”
          <button className="link" style={{ marginLeft: 8 }} onClick={() => setSel(null)}>clear</button>
        </div>
      )}
      <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
        <input className="input" placeholder={sel ? `Comment on the selected ${sel.target} text…` : "Add a note…"} value={newComment} style={{ flex: 1 }}
          onChange={(e) => setNewComment(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") postComment(); }} />
        <button className="btn" onClick={postComment}>Add</button>
      </div>

      {job.url && (<><div className="label">Posting</div><div><a href={job.url} target="_blank" rel="noreferrer">{job.url}</a></div></>)}
      {m.comp_estimate && (<><div className="label">Comp estimate</div><div>{m.comp_estimate}</div></>)}
      {m.why_fits && (<><div className="label">Why it fits</div><div>{m.why_fits}</div></>)}
      {m.watch_outs && (<><div className="label">Watch-outs</div><div>{m.watch_outs}</div></>)}
      {job.jd_text && (<><div className="label">Job description</div><div style={{ whiteSpace: "pre-wrap", color: "var(--muted)" }}>{job.jd_text.slice(0, 2000)}{job.jd_text.length > 2000 ? "…" : ""}</div></>)}
    </div>
  );
}
